import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SignOut, ArrowLeft } from "@phosphor-icons/react";
import { api } from "../lib/api";
import Overview from "../components/admin/Overview";
import Enquiries from "../components/admin/Enquiries";
import Payments from "../components/admin/Payments";
import Referrals from "../components/admin/Referrals";
import Messages from "../components/admin/Messages";
import Bookings from "../components/admin/Bookings";
import ReviewsAdmin from "../components/admin/ReviewsAdmin";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "enquiries", label: "Enquiries" },
  { id: "payments", label: "Payments" },
  { id: "referrals", label: "Referrals" },
  { id: "messages", label: "Messages" },
  { id: "bookings", label: "Bookings" },
  { id: "reviews", label: "Reviews" },
];

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("overview");
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    const poll = () => api.get("/conversations-unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      navigate("/admin/login");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 min-h-[72px] py-3 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <p className="font-serif text-xl text-primary tracking-tight">
              The Timeshare <em className="text-accent">Edit</em>
              <span className="ml-3 text-xs font-sans font-bold uppercase tracking-[0.2em] text-muted-foreground">Admin</span>
            </p>
            <nav className="flex items-center gap-1 bg-secondary/60 rounded-full p-1" data-testid="admin-tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${
                    tab === t.id ? "bg-primary text-primary-foreground" : "text-foreground/60 hover:text-primary"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" data-testid="admin-back-to-site" className="hidden sm:flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300">
              <ArrowLeft size={15} weight="bold" /> Site
            </Link>
            <span className="hidden md:inline text-xs text-foreground/45" data-testid="admin-signed-in-as">
              {user ? user.email : ""}
            </span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-5 py-2 rounded-full hover:bg-accent transition-colors duration-300"
            >
              <SignOut size={15} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        {tab === "overview" && <Overview />}
        {tab === "enquiries" && <Enquiries />}
        {tab === "payments" && <Payments />}
        {tab === "referrals" && <Referrals />}
        {tab === "messages" && <Messages />}
        {tab === "bookings" && <Bookings />}
        {tab === "reviews" && <ReviewsAdmin />}
      </main>
    </div>
  );
}
